import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMail, FiPhone, FiMapPin, FiClock, FiUser, FiMessageSquare } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    // Reset form
    setFormData({ name: '', email: '', subject: '', message: '' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-12"
      >
        <div className="flex justify-center mb-6">
          <img 
            src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751577427758-blob" 
            alt="Professor Klein" 
            className="w-24 h-24 rounded-full border-4 border-blue-600 shadow-lg object-cover"
          />
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Contact Professor Klein</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Have questions about the courses or need additional support? Professor Klein is here to help you succeed in your marketing journey.
        </p>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-12">
        {/* Contact Information */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-8"
        >
          <div className="bg-white rounded-lg shadow-md p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
            
            <div className="space-y-6">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiMail} className="text-blue-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Email</h3>
                  <p className="text-gray-600">professor.klein@university.edu</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiPhone} className="text-green-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Phone</h3>
                  <p className="text-gray-600">(555) 123-4567</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiMapPin} className="text-purple-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Office Location</h3>
                  <p className="text-gray-600">Business Building, Room 205<br />University Campus</p>
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiClock} className="text-orange-600 text-xl" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Office Hours</h3>
                  <p className="text-gray-600">
                    Monday - Wednesday: 2:00 PM - 4:00 PM<br />
                    Friday: 10:00 AM - 12:00 PM
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-8 border border-blue-200">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Quick Support</h3>
            <div className="space-y-3">
              <button className="w-full text-left p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3">
                <SafeIcon icon={FiMessageSquare} className="text-blue-600" />
                <span>Join Office Hours Discussion</span>
              </button>
              <button className="w-full text-left p-3 bg-white rounded-lg hover:bg-gray-50 transition-colors flex items-center space-x-3">
                <SafeIcon icon={FiUser} className="text-blue-600" />
                <span>Schedule One-on-One Meeting</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Contact Form */}
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Send a Message to Professor Klein</h2>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Your full name"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="your.email@university.edu"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2">
                Subject
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Select a subject</option>
                <option value="course-question">Course Question</option>
                <option value="assignment-help">Assignment Help</option>
                <option value="office-hours">Office Hours</option>
                <option value="general-inquiry">General Inquiry</option>
                <option value="technical-issue">Technical Issue</option>
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                required
                rows="5"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Please describe your question or concern in detail..."
              ></textarea>
            </div>

            <button
              type="submit"
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Send Message to Professor Klein
            </button>
          </form>
        </motion.div>
      </div>

      {/* Additional Resources */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className="mt-16 bg-white rounded-lg shadow-md p-8"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Additional Resources</h2>
        <div className="grid md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiMessageSquare} className="text-blue-600 text-2xl" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Student Forums</h3>
            <p className="text-gray-600 text-sm">Connect with classmates and discuss course materials</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiClock} className="text-green-600 text-2xl" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Study Groups</h3>
            <p className="text-gray-600 text-sm">Join or create study groups for collaborative learning</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiUser} className="text-purple-600 text-2xl" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Tutoring Services</h3>
            <p className="text-gray-600 text-sm">Access additional academic support and tutoring</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default Contact;